import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { CaptureItem } from './CaptureItem';
import { ManualAddCapture } from './ManualAddCapture';
import { SpawnSessionDialog } from './SpawnSessionDialog';
import { capturesApi } from '../../api/captures';
import { seedChatText } from '../Chat/nudgeActionCopy';
import { SAVED_IDEAS_CHANGED, notifySavedIdeasChanged } from './savedIdeasSync';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import ConfirmModal from '../Common/ConfirmModal';
import { useConfirm } from '../Common/useConfirm';

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' }
];

// Idea cards (series-planning-cards): recency axis. "View Latest" = active.
const IDEA_VIEW_OPTIONS = [
  { value: 'active', label: 'View Latest (Active)' },
  { value: 'parked', label: 'Parked' },
  { value: 'all', label: 'All Versions' }
];

// "Load more" page size for the captures list. Backend caps the `limit`
// query param at 200 regardless of this value.
const PAGE_SIZE = 50;

export function CapturesView({ scope, onNavigateToSession, onCaptureCountChange, captureType }) {
  const [captures, setCaptures] = useState([]);
  const [filter, setFilter] = useState('all');
  const [ideaView, setIdeaView] = useState('active'); // active | parked | all
  const [isLoading, setIsLoading] = useState(true);
  const [spawnDialog, setSpawnDialog] = useState(null);
  const [selectedLabelIds, setSelectedLabelIds] = useState([]);
  const { confirm, modalProps } = useConfirm();

  // Pagination — local, panel-scoped state (mirrors ChatInterface's
  // hasMoreMessages/messagesCursor pattern rather than a context provider).
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(null);

  const breakpoint = useBreakpoint();
  const pointerSensor = useSensor(PointerSensor);
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  const sensors = useSensors(
    ...(breakpoint !== 'mobile' ? [pointerSensor, keyboardSensor] : [])
  );

  // Filter params shared between the initial/fresh load and "load more" —
  // everything except limit/offset, which differ per call site.
  const buildFilterParams = useCallback(() => {
    const isIdeas = captureType === 'ideas';
    const entryType = isIdeas ? 'idea' : 'todo';
    // Idea cards carry no status; their recency axis is active/parked.
    // active -> parked=false, parked -> parked=true, all -> no parked filter.
    let parked;
    if (isIdeas) parked = ideaView === 'active' ? false : ideaView === 'parked' ? true : undefined;
    return {
      type: entryType,
      status: (isIdeas || filter === 'all') ? null : filter,
      parked,
      label_ids: selectedLabelIds.length > 0 ? selectedLabelIds : undefined,
      label_match: selectedLabelIds.length > 1 ? 'any' : undefined
    };
  }, [captureType, ideaView, filter, selectedLabelIds]);

  // Load captures — always a fresh, first-page fetch. Resets pagination back
  // to offset 0, so this doubles as the filter-change reset (criterion 7):
  // whenever filter/ideaView/captureType/selectedLabelIds change, the
  // effect below re-runs this because buildFilterParams (and therefore this
  // callback) changes identity.
  const loadCaptures = useCallback(async () => {
    if (!scope?.id) return;

    setIsLoading(true);
    setLoadMoreError(null);
    try {
      const params = { ...buildFilterParams(), limit: PAGE_SIZE, offset: 0 };
      const response = scope.type === 'story'
        ? await capturesApi.listStory(scope.id, params)
        : await capturesApi.listProject(scope.id, params);

      const captureData = response?.data || [];
      const responseTotal = response?.pagination?.total ?? captureData.length;
      setCaptures(captureData);
      setTotal(responseTotal);
      setOffset(captureData.length);
      onCaptureCountChange?.(responseTotal);
    } catch (error) {
      console.error('Failed to load captures:', error);
      setCaptures([]);
      setTotal(0);
      setOffset(0);
    } finally {
      setIsLoading(false);
    }
  }, [scope, buildFilterParams, onCaptureCountChange]);

  useEffect(() => {
    loadCaptures();
  }, [loadCaptures]);

  useEffect(() => {
    const onChanged = () => {
      if (captureType === 'ideas') loadCaptures();
    };
    window.addEventListener(SAVED_IDEAS_CHANGED, onChanged);
    return () => window.removeEventListener(SAVED_IDEAS_CHANGED, onChanged);
  }, [captureType, loadCaptures]);

  // Load the next batch and append it to the already-visible list. Failures
  // here surface as an inline retry affordance (see render below) — they
  // never replace or hide captures already loaded successfully.
  const handleLoadMore = async () => {
    if (!scope?.id || isLoadingMore) return;

    setIsLoadingMore(true);
    setLoadMoreError(null);
    try {
      const params = { ...buildFilterParams(), limit: PAGE_SIZE, offset };
      const response = scope.type === 'story'
        ? await capturesApi.listStory(scope.id, params)
        : await capturesApi.listProject(scope.id, params);

      const captureData = response?.data || [];
      const responseTotal = response?.pagination?.total ?? (offset + captureData.length);
      setCaptures(prev => [...prev, ...captureData]);
      onCaptureCountChange?.(responseTotal);
      setTotal(responseTotal);
      setOffset(prev => prev + captureData.length);
    } catch (error) {
      console.error('Failed to load more captures:', error);
      setLoadMoreError(error.message || 'Failed to load more captures');
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (captureId, newStatus) => {
    try {
      await capturesApi.update(captureId, { status: newStatus });
      loadCaptures();
    } catch (error) {
      console.error('Failed to update capture:', error);
    }
  };

  // Handle delete
  const handleDelete = async (captureId) => {
    const ok = await confirm({ title: 'Delete capture?', message: 'Delete this capture?', confirmLabel: 'Delete', destructive: true });
    if (!ok) return;
    try {
      await capturesApi.delete(captureId);
      notifySavedIdeasChanged({ captureId });
      if (captureType !== 'ideas') loadCaptures();
    } catch (error) {
      console.error('Failed to delete capture:', error);
    }
  };

  // Handle content edit
  const handleEdit = async (captureId, content) => {
    try {
      await capturesApi.update(captureId, { content });
      loadCaptures();
    } catch (error) {
      console.error('Failed to update capture:', error);
    }
  };

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = captures.findIndex(c => c.id === active.id);
    const newIndex = captures.findIndex(c => c.id === over.id);

    // Optimistic update
    const reorderedCaptures = arrayMove(captures, oldIndex, newIndex);
    setCaptures(reorderedCaptures);

    // Calculate after/before IDs
    const afterId = newIndex > 0 ? reorderedCaptures[newIndex - 1].id : null;
    const beforeId = newIndex < reorderedCaptures.length - 1
      ? reorderedCaptures[newIndex + 1].id
      : null;

    try {
      await capturesApi.reorder(active.id, afterId, beforeId);
    } catch (error) {
      console.error('Failed to reorder capture:', error);
      loadCaptures(); // Revert on error
    }
  };

  // Handle spawn session — payload carries name + session type + focus area
  const handleSpawnSession = async (payload) => {
    if (!spawnDialog) return;

    try {
      const capture = spawnDialog.capture;
      const isIdea = capture.entry_type === 'idea' || capture.entry_type === 'snippet';
      const seeded = isIdea
        ? { ...payload, seed_text: seedChatText(capture.content, capture.source_session_name) }
        : payload;
      const result = await capturesApi.spawnSession(capture.id, seeded);
      setSpawnDialog(null);
      loadCaptures();
      onNavigateToSession?.(result?.data?.session?.id);
    } catch (error) {
      console.error('Failed to spawn session:', error);
    }
  };

  // Handle action button click
  const handleAction = (capture, action) => {
    if (action === 'resume') {
      // Find active session and navigate
      const activeSession = capture.sessions?.find(s => s.status === 'active');
      if (activeSession) {
        onNavigateToSession?.(activeSession.session_id);
      } else {
        // No active session, offer to start new one
        setSpawnDialog({ capture, action: 'start' });
      }
    } else {
      // Start or revisit - show spawn dialog
      setSpawnDialog({ capture, action });
    }
  };

  // Handle manual add
  const handleCaptureCreated = () => {
    loadCaptures();
  };

  // Handle label click (filter by label)
  const handleLabelClick = (label) => {
    setSelectedLabelIds(prev => {
      const isSelected = prev.includes(label.id);
      if (isSelected) {
        // Remove from filter
        return prev.filter(id => id !== label.id);
      } else {
        // Add to filter
        return [...prev, label.id];
      }
    });
  };

  // Clear label filters
  const handleClearLabelFilters = () => {
    setSelectedLabelIds([]);
  };

  if (isLoading) {
    return <div className="captures-view__loading">Loading captures...</div>;
  }

  return (
    <div className="captures-view">
      {/* Filter — To Do tab: status View filter. Ideas tab: active/parked
          recency filter ("View Latest" = active). Label filter on both. */}
      {(captureType !== 'ideas' || true) && (
        <div className="captures-view__filter">
          {captureType === 'actions' && (
            <>
              <label>View:</label>
              <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                {FILTER_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </>
          )}
          {captureType === 'ideas' && (
            <>
              <label>Show:</label>
              <select value={ideaView} onChange={(e) => setIdeaView(e.target.value)}>
                {IDEA_VIEW_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </>
          )}
          {selectedLabelIds.length > 0 && (
            <button
              className="captures-view__clear-labels"
              onClick={handleClearLabelFilters}
            >
              Clear Label Filter ({selectedLabelIds.length})
            </button>
          )}
        </div>
      )}

      {/* Capture List */}
      {captures.length === 0 ? (
        <div className="captures-view__empty">
          {(filter === 'all' || captureType !== 'actions') ? (
            <>
              <p className="captures-view__empty-title">
                No {captureType === 'ideas' ? 'ideas' : 'To Dos'} yet
              </p>
              <p className="captures-view__empty-hint">
                {captureType === 'ideas' ? 'Add an idea…' : 'Add a To Do…'}
              </p>
            </>
          ) : (
            <>
              <p className="captures-view__empty-title">No To Dos match this filter</p>
              <button
                className="captures-view__show-all"
                onClick={() => setFilter('all')}
              >
                Show All
              </button>
            </>
          )}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={captures.map(c => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="captures-view__list">
              {captures.map(capture => (
                <CaptureItem
                  key={capture.id}
                  capture={capture}
                  onStatusChange={handleStatusChange}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onAction={handleAction}
                  onLabelClick={handleLabelClick}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Pagination — "load more" append pattern. Hidden entirely once every
          matching capture is already loaded (criterion 5 / 9: no UI at all
          for lists that fit on one page). */}
      {captures.length < total && (
        <div className="captures-view__load-more">
          {loadMoreError ? (
            <div className="captures-view__load-more-error">
              {loadMoreError}
              <button
                className="captures-view__load-more-retry"
                onClick={handleLoadMore}
                disabled={isLoadingMore}
              >
                Retry
              </button>
            </div>
          ) : (
            <button
              className="captures-view__load-more-button"
              onClick={handleLoadMore}
              disabled={isLoadingMore}
            >
              {isLoadingMore ? 'Loading…' : `Load more (${captures.length} of ${total})`}
            </button>
          )}
        </div>
      )}

      {/* Manual Add */}
      <ManualAddCapture
        scope={scope}
        captureType={captureType}
        onCaptureCreated={handleCaptureCreated}
      />

      {/* Spawn Session Dialog */}
      {spawnDialog && (
        <SpawnSessionDialog
          capture={spawnDialog.capture}
          action={spawnDialog.action}
          onConfirm={handleSpawnSession}
          onCancel={() => setSpawnDialog(null)}
        />
      )}
      <ConfirmModal {...modalProps} />
    </div>
  );
}

export default CapturesView;
